import React, { useState, useEffect, useRef } from 'react';
import { X, Map, List, LayoutDashboard, CalendarCheck, Sparkles, User, Users, Settings, Network, ClipboardList, Building, Shield } from 'lucide-react';
import SimpleFilters from './SimpleFilters';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

const NAV_ITEMS = [
  { id: 'map',       label: 'Map',       icon: Map },
  { id: 'visits',    label: 'Visits',    icon: CalendarCheck },
  { id: 'list',      label: 'List',      icon: List },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'graph',     label: 'Graph',     icon: Network },
  { id: 'briefing',  label: 'AI Brief',  icon: Sparkles },
  { id: 'profile',   label: 'Profile',   icon: User },
  { id: 'settings',  label: 'Settings',  icon: Settings },
];

const MobileNav = ({
  // { traffic, population }: layers with real data (utils/areaLayers.js)
  areaLayers = { traffic: false, population: false },
  areaDatasets = [],
  activeDatasetLayer = null,
  onDatasetLayerChange,
  isOpen,
  onClose,
  currentView,
  onViewChange,
  onGetBriefing,
  currentUser,
  visitCount = 0,
  workCount = 0,
  isBriefingActive = false,
  // filter props
  onSimpleFilterChange,
  currentSimpleFilters,
  isH3Visible,
  h3ActiveView,
  onH3ViewChange,
  h3RevenueFilterRange,
  maxRevenueValue,
  onH3RevenueRangeChange,
  h3SalesFilterRange,
  h3TrafficFilterRange,
  maxSalesValue,
  maxTrafficValue,
  onH3SalesRangeChange,
  onH3TrafficRangeChange,
  onResetH3Filters,
  // nav props
  onShowProfile,
  onShowSettings,
  onShowMySettings,
  onShowTeamSettings,
}) => {
  const [activeSection, setActiveSection] = useState('map');
  const grabRef = useRef(null);
  const dragStartY = useRef(null);
  const dragCurrentY = useRef(null);

  // Reset active section when sheet opens
  useEffect(() => {
    if (isOpen) setActiveSection(currentView || 'map');
  }, [isOpen, currentView]);

  // Swipe-down to dismiss, bound to the handle and header only. Bound to the
  // whole sheet it also fired while scrolling the filter body: scrolling back
  // up is a downward drag, so any flick past 80px shut the menu mid-scroll.
  // isOpen is a dependency because the sheet is not mounted while closed —
  // without it, attaching relied on the parent happening to re-render.
  useEffect(() => {
    const el = grabRef.current;
    if (!isOpen || !el) return;

    const onStart = (e) => {
      dragStartY.current = e.touches[0].clientY;
    };
    const onMove = (e) => {
      dragCurrentY.current = e.touches[0].clientY;
    };
    const onEnd = () => {
      if (dragStartY.current !== null && dragCurrentY.current !== null) {
        const dy = dragCurrentY.current - dragStartY.current;
        if (dy > 80) onClose();
      }
      dragStartY.current = null;
      dragCurrentY.current = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [isOpen, onClose]);

  const handleNavTap = (id) => {
    setActiveSection(id);
    if (id === 'briefing') {
      onGetBriefing?.();
      onClose();
      return;
    }
    if (id === 'profile') {
      onShowProfile?.();
      onClose();
      return;
    }
    if (id === 'settings') {
      // Personal settings for everyone; an admin's org settings are in the Admin Hub.
      onShowMySettings?.();
      onClose();
      return;
    }
    if (id === 'team-settings') {
      onShowTeamSettings?.();
      onClose();
      return;
    }
    // For map/list/visits/dashboard/graph/tasks: navigate + close
    if (['map', 'list', 'visits', 'dashboard', 'graph', 'tasks', 'accounts', 'admin'].includes(id)) {
      onViewChange(id);
      onClose();
    }
  };

  // Managers (area_manager/hq_manager/admin) get a "Team" entry that opens Team Settings.
  const isManagerRole = ['area_manager', 'hq_manager', 'admin'].includes(currentUser?.role);
  // Tasks (with handovers inside) for everyone, after Map, Visits and List.
  const withAccounts = ['key_account_manager', 'hq_manager', 'admin'].includes(currentUser?.role)
    ? [{ id: 'accounts', label: 'Customers', icon: Building }] : [];
  const baseItems = [...NAV_ITEMS.slice(0, 3), { id: 'tasks', label: 'Tasks', icon: ClipboardList }, ...withAccounts, ...NAV_ITEMS.slice(3)];
  // Admins get the Admin Hub (where their team and org settings live) instead.
  const managerEntry = currentUser?.role === 'admin'
    ? { id: 'admin', label: 'Admin Hub', icon: Shield }
    : { id: 'team-settings', label: 'Team', icon: Users };
  const navItems = isManagerRole
    ? [...baseItems.slice(0, -1), managerEntry, baseItems[baseItems.length - 1]]
    : baseItems;

  const renderContent = () => {
    switch (activeSection) {
      case 'map':
      case 'list':
        return (
          <div className="p-4 space-y-4">
            {onSimpleFilterChange && currentSimpleFilters && (
              <SimpleFilters
                onFilterChange={onSimpleFilterChange}
                currentFilters={currentSimpleFilters}
              />
            )}
            {activeSection === 'map' && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Map Layers</div>
                {[
                  { key: 'sales', label: 'Sales Data' },
                  areaLayers.traffic && { key: 'traffic', label: 'Traffic Density' },
                  { key: 'revenue', label: 'Revenue (Transactions)' },
                ].filter(Boolean).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={h3ActiveView === key && isH3Visible}
                      onChange={() => onH3ViewChange?.(key)}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                    />
                    <span className="text-sm text-gray-50">{label}</span>
                  </label>
                ))}
                {areaDatasets.length > 0 && (
                  <div className="pt-2" data-testid="area-dataset-layers">
                    <div className="px-2 pb-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Your area data</div>
                    {areaDatasets.flatMap(ds => ds.metrics.map(m => (
                      <label key={`${ds.id}:${m.key}`} className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={Boolean(activeDatasetLayer && activeDatasetLayer.datasetId === ds.id && activeDatasetLayer.key === m.key)}
                          onChange={() => onDatasetLayerChange?.(ds.id, m.key)}
                          className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                        />
                        <span className="text-sm text-gray-50 truncate" title={ds.name}>
                          {ds.metrics.length > 1 ? `${ds.name} · ${m.label}` : ds.name}
                        </span>
                      </label>
                    )))}
                  </div>
                )}
                {isH3Visible && h3SalesFilterRange && h3TrafficFilterRange && (
                  <div className="p-3 bg-gray-800 rounded-lg space-y-4 mt-2">
                    {h3ActiveView === 'sales' && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          SALES: ${h3SalesFilterRange[0].toLocaleString()} – ${h3SalesFilterRange[1].toLocaleString()}
                        </div>
                        <Slider
                          range min={0} max={maxSalesValue} value={h3SalesFilterRange}
                          onChange={onH3SalesRangeChange}
                          trackStyle={[{ backgroundColor: '#10b981' }]}
                          handleStyle={[{ borderColor: '#10b981', backgroundColor: '#10b981' }, { borderColor: '#10b981', backgroundColor: '#10b981' }]}
                          railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                        />
                      </div>
                    )}
                    {h3ActiveView === 'traffic' && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          TRAFFIC: {h3TrafficFilterRange[0]} – {h3TrafficFilterRange[1]}
                        </div>
                        <Slider
                          range min={0} max={maxTrafficValue} value={h3TrafficFilterRange}
                          onChange={onH3TrafficRangeChange}
                          trackStyle={[{ backgroundColor: '#10b981' }]}
                          handleStyle={[{ borderColor: '#10b981', backgroundColor: '#10b981' }, { borderColor: '#10b981', backgroundColor: '#10b981' }]}
                          railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                        />
                      </div>
                    )}
                    {h3ActiveView === 'revenue' && h3RevenueFilterRange && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          REVENUE: ¥{h3RevenueFilterRange[0].toLocaleString()} – ¥{h3RevenueFilterRange[1].toLocaleString()}
                        </div>
                        <Slider
                          range min={0} max={maxRevenueValue} value={h3RevenueFilterRange}
                          onChange={onH3RevenueRangeChange}
                          trackStyle={[{ backgroundColor: '#f59e0b' }]}
                          handleStyle={[{ borderColor: '#f59e0b', backgroundColor: '#f59e0b' }, { borderColor: '#f59e0b', backgroundColor: '#f59e0b' }]}
                          railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { onResetH3Filters?.(); onClose(); }}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold text-white text-sm transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[54]"
        onClick={onClose}
      />

      {/* Sheet */}
      {/* paddingBottom clears the home indicator so no control sits in the strip
          iOS reserves for its own gestures. The sheet's background fills that
          strip, so it still reads as reaching the bottom of the screen. */}
      <div
        className="fixed inset-x-0 bottom-0 z-[55] bg-gray-900 rounded-t-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '92vh', paddingBottom: 'var(--safe-bottom)' }}
      >
        {/* Grab area: drag handle + header. The dismiss gesture lives here. */}
        <div ref={grabRef} className="flex-shrink-0">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 rounded-full bg-gray-600" />
          </div>

          {/* Header */}
          <div className="px-4 pb-3 flex items-center justify-between border-b border-gray-800">
            <span className="text-gray-50 font-bold text-lg">Menu</span>
            <button onClick={onClose} aria-label="Close menu" className="p-2 text-gray-400 hover:text-gray-50 rounded-full hover:bg-gray-800 transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Destinations. A wrapping grid rather than a horizontal scroller: with
            nine entries the scroller pushed roughly 300px of the menu — Profile,
            Team, Settings — off the edge with no affordance that more existed.
            It was also unusable in the one case that collapses the sheet
            (opening the menu from Visits, which renders no body): the row then
            sat flush with the bottom of the screen, inside the iOS
            home-indicator zone where the system swallows sideways drags. A grid
            needs no scrolling, so every entry is reachable however tall the
            sheet is. */}
        <div className="grid grid-cols-4 gap-2 px-4 py-3 flex-shrink-0 border-b border-gray-800">
          {navItems.map(({ id, label, icon: Icon }) => {
            const isActive = activeSection === id;
            const badgeCount = id === 'visits' ? visitCount : id === 'tasks' ? workCount : 0;
            const showBadge = badgeCount > 0;
            return (
              <button
                key={id}
                onClick={() => handleNavTap(id)}
                className={`relative flex flex-col items-center justify-center gap-1 px-1 py-2.5 rounded-xl text-[11px] font-semibold leading-tight transition-colors ${
                  isActive
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-800 text-gray-300 active:bg-gray-700'
                }`}
              >
                <Icon size={20} />
                <span className="w-full truncate text-center">{label}</span>
                {showBadge && (
                  <span className="absolute top-1 right-1 bg-purple-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto scroll-pane">
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default MobileNav;
